<script lang="ts">
  import DropdownContent, { filterKey } from "./internal/DropdownContent.svelte";
  import { getContext, setContext, onDestroy, onMount } from "svelte";
  import { writable } from "svelte/store";
  import { createPopperActions } from "svelte-popperjs";
  import { clickOutside } from "$actions/clickOutside";
  import { portal } from "$actions/portal.svelte";
  import { getMediaQuery } from "$utils/mediaQuery";
  import { browser } from "$app/environment";
  import { createEventDispatcher } from "svelte";
  import type { Writable } from "svelte/store";

  const dispatch = createEventDispatcher();

  export let open = false;
  export let fullWidth = false;
  export let maxWidth: number | undefined = undefined; // max width of dropdown
  export let preventClose = false; // this prevent setting the innerOpenID to the current dropdown in order to mantain the open state of the dropdown

  // record self to close when other siblings are opened
  const self = Symbol();
  const INNER_ID = "innerOpenID";
  const isDesktop = getMediaQuery("desktop");

  // tracks which nested dropdown is currently open to ensure only one can be open at a time
  let innerOpenID = writable<symbol | null>(null);
  let triggerRef: HTMLSpanElement;
  let observer: ResizeObserver;

  // get/set context if parent/child
  if (getContext(INNER_ID) && !preventClose) {
    innerOpenID = getContext(INNER_ID);
  } else {
    setContext(INNER_ID, innerOpenID);
  }

  const filter = getContext<Writable<string>>(filterKey);
  $: isInnerDropdown = $filter !== undefined;

  // We use the createPopperActions function to create the popperRef and popperContent variables.
  // The popperRef represents the target element.
  // The popperContent represents the element that will be positioned relative to the target element.
  $: [popperRef, popperContent, getInstance] = createPopperActions({
    placement: isInnerDropdown ? "right-start" : "bottom-start",
  });
  const extraOpts = {
    modifiers: [
      {
        name: "offset",
        options: { offset: [0, 3] },
      },
      {
        name: "preventOverflow",
        options: {
          altAxis: true, // allow the dropdown to be positioned on the opposite axis if there is not enough space on the current axis
          padding: 1, // add padding to the dropdown to prevent it from being positioned too close to the edge of the screen
        },
      },
    ],
  };

  $: {
    // reposition dropdown when filter changes
    $filter;
    getInstance()?.update();
  }

  // close self if a sibling is opened
  $: if (isInnerDropdown && $innerOpenID !== self) {
    open = false;
  }

  onMount(() => {
    if (!triggerRef) {
      // triggerRef might be undefined if this code runs before Svelte has fully rendered and bound the DOM element (via bind:this).
    }
    // reposition dropdown when trigger element resizes
    observer = new ResizeObserver(() => getInstance()?.update());
    observer.observe(triggerRef);
  });

  onDestroy(() => {
    observer?.disconnect();
  });
</script>

<!-- tried making this conditonal on there being slot content but this svelte issue prevents it: https://github.com/sveltejs/svelte/issues/5312 -->
{#if browser}
  <span
    use:popperRef
    on:click|preventDefault|stopPropagation={() => {
      open = !open;
      if (open) $innerOpenID = self;
    }}
    on:keydown|preventDefault|stopPropagation={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        open = !open;
        if (open) $innerOpenID = self;
      }
      if (event.key === "Tab") {
        // close dropdown when navigating out via keyboard
        open = false;
      }
    }}
    on:mouseenter={() => {
      if (!$isDesktop) return;
      if (!isInnerDropdown) return;
      open = true;
      $innerOpenID = self;
    }}
    on:deactivate={() => (open = false)}
    bind:this={triggerRef}
    class="block"
    class:w-fit={!isInnerDropdown && !fullWidth}
    role="presentation"
  >
    <slot name="trigger" active={open} />
  </span>
{/if}

{#if open}
  <!--
  1: use:portal -> comes from svelte-portal package. Used to attach an element to a DOM node. This is done using the portal action.
    By default, if no parameter is passed, it attaches the current element to the body.
  2: use:popperContent -> element that will be positioned relative to the target one.
    Why? aside from all the benefits of using popperjs for automatic positioning, combining this with portal the given solution prevents stacking issues.
  -->
  <!-- sets the min dropdown content width value to the minimum between the trigger element's width and the maximum width passed as prop -->
  <div
    use:portal={{ target: "body", hasBackLayer: !isInnerDropdown }}
    use:popperContent={extraOpts}
    use:clickOutside={() => {
      open = false;
      triggerRef.blur();
      dispatch("close");
    }}
    on:close={() => {
      open = false;
      triggerRef.blur();
      dispatch("close");
    }}
    style:min-width={`min(${triggerRef?.getBoundingClientRect().width}px,${maxWidth}px)`}
  >
    <DropdownContent>
      <slot />
    </DropdownContent>
  </div>
{/if}
